/**
 * One decision function for "what should this page render right now", shared by
 * every list and detail surface.
 *
 * ## Why this exists
 *
 * React Query's default `networkMode` is `'online'`. When the browser goes
 * offline a query does NOT error and does NOT keep loading: query-core's
 * `canFetch()` refuses to start it, so it sits at
 * `status: 'pending', fetchStatus: 'paused'`. And because
 * `isLoading = isPending && fetchStatus === 'fetching'` (queryObserver.ts),
 * a paused query reports:
 *
 *     isLoading === false, isError === false, data === undefined
 *
 * Every page here previously branched on exactly `isLoading` / `isError` /
 * `length === 0`, so an offline user fell through all three guards and landed
 * on the *empty state*: "No accounts yet. Add a bank, brokerage, ...", with an
 * add button. The app confidently reported an empty portfolio to someone whose
 * data was merely unreachable. That is the failure this module removes.
 *
 * It matters more here than in most apps because the service worker precaches
 * the app shell but deliberately caches no API data (a financial portal must
 * not leave account JSON in Cache Storage past sign-out, see vite.config.ts).
 * So offline the app loads *completely* and looks perfectly healthy, with
 * nothing but empty states behind it.
 *
 * ## Precedence
 *
 * `offline` is checked before `error` because it is the more actionable
 * message when both are true (a query that errored just before the connection
 * dropped is still, right now, an offline problem).
 *
 * ## A failure state only takes over when there is nothing to show
 *
 * Both `offline` and `error` require `data === undefined`. Cached data is not
 * fabricated: it is a real answer the server gave a moment ago, which is a
 * different thing entirely from the `$0` this module exists to suppress (a
 * number synthesized from an array that never arrived). Blanking a
 * fully-rendered page because a background refetch failed would replace good
 * information with none.
 *
 * This mirrors what the most carefully written pages already did: Portfolio
 * guarded on `netWorthError && !data`, and AccountDetail / EntityDetail /
 * LiabilityDetail each on `isError && !record`. An earlier revision of this
 * module dropped that qualifier, which silently converted a transient refetch
 * failure into a full-page error on exactly the surfaces where someone had
 * previously thought hardest about it. The rule is centralized here so the
 * simple list pages get the same protection rather than each re-deciding.
 *
 * ## The one exception: `{ authoritative: true }`
 *
 * An access list is the case where the rule above is wrong. The cached value is not
 * information a person can still use, it is a CLAIM about who can read a family's
 * documents, and the surface offers a corrective action (Add, Remove, a choice marked "Now")
 * gated on that same value. Rendering a stale list as loaded, with those controls live, lets
 * an admin act on, and conclude from, a list we could not just confirm (steward-app#843,
 * #863). A caller opts in per call with `{ authoritative: true }`: under it a query that is
 * `isError` or `isPaused` is treated as failed whether or not `data` is cached, with the
 * same precedence (`offline` before `error`). The default call is unchanged, and the
 * `data === undefined` rule above still governs every call that does not pass the option.
 * Pinned in both directions in `queryState.test.ts`.
 */

export type QueryDisplayState = 'loading' | 'offline' | 'error' | 'empty' | 'ready';

/** The subset of a React Query result this needs. Structural, so a
 *  `UseQueryResult` of any shape satisfies it without a cast. */
export interface QueryLike {
  isLoading: boolean;
  isPaused: boolean;
  isError: boolean;
  data: unknown;
}

export interface QueryDisplayOptions {
  /** This read is an authoritative claim (an access list): cached data is not an answer. */
  authoritative?: boolean;
}

/**
 * Reduce one or more queries plus a caller-computed emptiness test to a single
 * render state.
 *
 * Always takes an array, including for a single query (`[accountsQ]`), so
 * multi-query pages (Portfolio, Dashboard, Assets) and single-query pages
 * branch through identical logic.
 *
 * @param queries  Every query the surface's content depends on.
 * @param isEmpty  Whether the *derived* result is empty, computed by the caller
 *                 (it knows whether that means `items.length === 0`, a null
 *                 detail record, or something else).
 * @param options  `authoritative: true` makes cached data not an answer: see
 *                 "The one exception" in the header. Omit it for an ordinary
 *                 read surface.
 */
export function queryDisplayState(
  queries: QueryLike[],
  isEmpty: boolean,
  options: QueryDisplayOptions = {},
): QueryDisplayState {
  // Under `authoritative`, a failed or paused read is a failure even over cached data.
  const cachedIsAnAnswer = options.authoritative !== true;
  // Paused with nothing cached: there is no data to show and none is coming
  // until the connection returns. Checked against `data === undefined` and not
  // `isPaused` alone so a *background* refetch that paused while cached data is
  // already on screen keeps showing that data (the app-level banner carries the
  // "this may be out of date" message in that case).
  if (queries.some(q => q.isPaused && (!cachedIsAnAnswer || q.data === undefined))) return 'offline';
  if (queries.some(q => q.isError && (!cachedIsAnAnswer || q.data === undefined))) return 'error';
  if (queries.some(q => q.isLoading)) return 'loading';

  // Nothing arrived, but nothing went wrong either. Two ways to land here:
  // a *disabled* query (`enabled: !!token` with no token yet) reports all four
  // flags false with `data === undefined`, and so does a 200 whose body was
  // malformed enough that the queryFn resolved without the expected array.
  //
  // Falling through to `empty` in that case would state a fact about content
  // that was never received — which on the ACL surfaces means rendering
  // "Open, anyone with access can see this" off a read that never happened,
  // the exact unverified claim this module exists to prevent. `loading` is the
  // honest answer: no result yet, and none of the failure modes apply.
  if (queries.some(q => q.data === undefined)) return 'loading';

  return isEmpty ? 'empty' : 'ready';
}
